import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import { STORE_URL_HEADER } from "@redotech/shopify-sdk/rpc/custom-http-headers";
import { createContext, memo, ReactNode, useContext } from "react";
import { z } from "zod";
import { RedoShopifyRpcClientContext } from "../../client/redo-shopify-rpc-client";
import { ZipCodeContext } from "./zip-code-context";

const DELIVERY_ESTIMATE_STORAGE_KEY = "redo-delivery-estimate";
const DELIVERY_ESTIMATE_EXPIRY_HOURS = 1; // 1 hour expiry

const deliveryEstimateStorageParser = z.object({
  zipCode: z.string(),
  estimatedDeliveryDateMsSinceEpoch: z.number(),
});
type DeliveryEstimateStorage = z.infer<typeof deliveryEstimateStorageParser>;

interface DateEstimateContextValue {
  estimatedDeliveryDate: Date | undefined;
  isLoading: boolean;
}

export const DateEstimateContext = createContext<DateEstimateContextValue>({
  estimatedDeliveryDate: undefined,
  isLoading: false,
});

export const DateEstimateProvider = memo(function DateEstimateProvider({
  children,
  storeUrl,
}: {
  children: ReactNode | ReactNode[];
  storeUrl: string;
}) {
  const shopifyRpcClient = useRequiredContext(RedoShopifyRpcClientContext);
  const zipContext = useContext(ZipCodeContext);

  const estimateLoad = useLoad<Date | undefined>(async () => {
    if (!zipContext?.savedZipCode || !storeUrl) {
      return;
    }

    // Check cache first
    const cacheKey = `${DELIVERY_ESTIMATE_STORAGE_KEY}-${zipContext.savedZipCode}`;
    const cachedData = getLocalStorageWithExpiry(cacheKey);
    const cachedParsed = deliveryEstimateStorageParser.safeParse(cachedData);

    if (
      cachedParsed.success &&
      cachedParsed.data.zipCode === zipContext.savedZipCode
    ) {
      return new Date(cachedParsed.data.estimatedDeliveryDateMsSinceEpoch);
    }

    // Make RPC request if not cached
    const response = await shopifyRpcClient.getDeliveryEstimate(
      {
        zipCode: zipContext.savedZipCode,
        storeUrl_forCacheRoutingOnly: storeUrl,
      },
      { method: "GET", requestHeaders: { [STORE_URL_HEADER]: storeUrl } },
    );

    // Cache the response
    if (response.estimatedDeliveryDate) {
      const dataToCache: DeliveryEstimateStorage = {
        zipCode: zipContext.savedZipCode,
        estimatedDeliveryDateMsSinceEpoch:
          response.estimatedDeliveryDate.getTime(),
      };
      setLocalStorageWithExpiry(
        cacheKey,
        dataToCache,
        DELIVERY_ESTIMATE_EXPIRY_HOURS,
      );
    }

    return response.estimatedDeliveryDate;
  }, [zipContext?.savedZipCode, storeUrl]);

  const dateEstimateState: DateEstimateContextValue = {
    estimatedDeliveryDate: estimateLoad.value,
    isLoading: estimateLoad.pending,
  };

  return (
    <DateEstimateContext.Provider value={dateEstimateState}>
      {children}
    </DateEstimateContext.Provider>
  );
});
